import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DocumentRowUi, RoleOptionUi } from "../../types/documentManagementUiTypes";
import {
  normalizeRoleAccessForSave,
  parseRoleAccess,
  roleAccessCompactDisplayLabel,
  roleAccessLabel,
} from "../../utils/documentManagementUiUtils";
import { DocumentRoleAccessAutocomplete } from "../shared/DocumentRoleAccessAutocomplete";

export type DocumentLibraryRoleCellProps = {
  row: DocumentRowUi;
  roleOptions: RoleOptionUi[];
  disabled?: boolean;
  saving?: boolean;
  onSave: (row: DocumentRowUi, roleAccess: string[]) => Promise<boolean>;
};

export function DocumentLibraryRoleCell({
  row,
  roleOptions,
  disabled = false,
  saving = false,
  onSave,
}: DocumentLibraryRoleCellProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draftRoles, setDraftRoles] = useState<string[]>(() =>
    parseRoleAccess(row.role_access)
  );

  const initialRoles = useMemo(
    () => parseRoleAccess(row.role_access),
    [row.role_access]
  );

  useEffect(() => {
    if (!open) {
      setDraftRoles(initialRoles);
    }
  }, [initialRoles, open]);

  const display = roleAccessCompactDisplayLabel(row.role_access, t);
  const fullLabel = roleAccessLabel(row.role_access);
  const dialogId = open ? `dm-role-edit-dialog-${row.id}` : undefined;

  const normalizedInitial = useMemo(
    () =>
      JSON.stringify(
        [...normalizeRoleAccessForSave(initialRoles, roleOptions)].sort()
      ),
    [initialRoles, roleOptions]
  );
  const normalizedDraft = useMemo(
    () =>
      JSON.stringify(
        [...normalizeRoleAccessForSave(draftRoles, roleOptions)].sort()
      ),
    [draftRoles, roleOptions]
  );

  const saveDisabled =
    saving || normalizedDraft === normalizedInitial || draftRoles.length === 0;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled || saving) return;
    event.stopPropagation();
    setDraftRoles(initialRoles);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDraftRoles(initialRoles);
  };

  const handleSave = async () => {
    const ok = await onSave(row, draftRoles);
    if (ok) {
      setOpen(false);
    }
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        className="dm-role-cell-trigger"
        onClick={handleOpen}
        disabled={disabled || saving}
        title={fullLabel}
        aria-haspopup="dialog"
        aria-expanded={open ? "true" : undefined}
        aria-controls={dialogId}
        aria-label={t("opva.dmEditRoles", { defaultValue: "Edit roles" })}
      >
        <Typography
          variant="body2"
          component="span"
          className="dm-role-cell-trigger__text"
        >
          {display}
        </Typography>
        <EditOutlinedIcon
          fontSize="small"
          className="dm-role-cell-trigger__icon"
          aria-hidden
        />
      </Box>
      <Dialog
        id={dialogId}
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        className="dm-role-edit-dialog"
      >
        <DialogTitle>
          {t("opva.dmEditRolesTitle", { defaultValue: "Edit roles" })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="bodySmall" color="text.secondary" sx={{ mb: 1 }}>
            {row.original_file_name}
          </Typography>
          <DocumentRoleAccessAutocomplete
            roleAccessRaw={JSON.stringify(draftRoles)}
            roleOptions={roleOptions}
            disabled={saving || !roleOptions.length}
            onChange={setDraftRoles}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={saving}>
            {t("opva.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            disabled={saveDisabled}
            onClick={() => void handleSave()}
          >
            {t("opva.save", { defaultValue: "Save" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
